import FileUploadMultiple from "components/FileUploadMultiple";
import { useTooltip } from "context/TooltipProvider";
import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchExhibitConditions } from "services/basedataServices";
import {
  addExhibit,
  editExhibit,
  editExhibit2,
} from "services/incidentService";
import {
  CrimeReference,
  Exhibit,
  ExhibitCondition,
  Investigation,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  investigation: Investigation;
  incident?: CrimeReference;
  exhibit?: Exhibit;
  isReturn?: boolean;
  onClose: (refetch: boolean) => void;
};

const defaultEvidence: Exhibit = {
  uu_id: 0,
  Incident_Registration_number: "",
  CR_number: "",
  tool_type: "",
  description: "",
  collected_by: "",
  unique_identification_code: "",
  reference_number: "",
  serial_number: "",
  location_region: "",
  location_zone: "",
  location_woreda: "",
  location_housenumber: "",
  location_site: "",
  person_with_whom: "",
  owner: "",
  quantity_written: "",
  exibit_condition: "",
  person_protect_fullname: "",
  person_protect_region: "",
  person_protect_zone: "",
  person_protect_woreda: "",
  person_protect_housenumber: "",
  person_protect_phonenumber: "",
  person_protect_site: "",
  government_protect_name: "",
  government_protect_region: "",
  government_protect_zone: "",
  government_protect_woreda: "",
  government_protect_housenumber: "",
  government_protect_phonenumber: "",
  government_protect_site: "",
  exhibit_condition_when_submitted: "",
  exhibit_attachment_files: [],
  crimereference: 0,
  is_sample_collected: false,
  sample_collected_by: "",
  sample_collection_date: undefined,
  sample_amount: "",
  sample_location: "",
  analysis_result: "",
  is_sample_returned: false,
  sample_return_date: undefined,
};

const ExhibitForm: FC<Props> = ({
  isOpen,
  action,
  investigation,
  incident,
  exhibit,
  isReturn = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<Exhibit>(
    exhibit ?? defaultEvidence,
  );
  const [assetType, setAssetType] = useState<"Person" | "Government">(
    exhibit?.person_protect_fullname ||
      exhibit?.person_protect_region ||
      exhibit?.person_protect_zone
      ? "Person"
      : "Government",
  );

  useEffect(() => {
    if (exhibit && (investigation || incident)) {
      setFormState(exhibit);
      setAssetType(
        exhibit.person_protect_fullname ||
          exhibit.person_protect_region ||
          exhibit.person_protect_zone
          ? "Person"
          : "Government",
      );
    }
  }, [exhibit, investigation, incident]);

  const [baseData, setBaseData] = useState<{
    exhibitConditions: ExhibitCondition[];
  }>({
    exhibitConditions: [],
  });

  useEffect(() => {
    fetchExhibitConditions().then((data) =>
      setBaseData((prev) => ({
        ...prev,
        exhibitConditions: data as unknown as ExhibitCondition[],
      })),
    );
  }, []);

  // Handle asset type change and clear the other fields
  const handleAssetTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "Person" | "Government";
    setAssetType(value);

    setFormState((prev) => {
      if (value === "Person") {
        // Clear government fields
        return {
          ...prev,
          government_protect_name: "",
          government_protect_region: "",
          government_protect_zone: "",
          government_protect_woreda: "",
          government_protect_housenumber: "",
          government_protect_phonenumber: "",
          government_protect_site: "",
        };
      } else {
        // Clear person fields
        return {
          ...prev,
          person_protect_fullname: "",
          person_protect_region: "",
          person_protect_zone: "",
          person_protect_woreda: "",
          person_protect_housenumber: "",
          person_protect_phonenumber: "",
          person_protect_site: "",
        };
      }
    });
  };

  useEffect(() => {
    if (exhibit && (investigation || incident)) {
      setFormState(exhibit);
    }
  }, [exhibit, investigation, incident]);

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "date"
            ? value
            : value,
    }));
  };

  const validateForm = () => {
    if (!formState.tool_type) return "Tool Type is Required.";
    else if (!formState.collected_by) return "Collected By is Required.";
    else if (!formState.date_collected) return "Date Collected is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction = action === "Edit" ? editExhibit : addExhibit;
    submitAction({
      ...formState,
      investigation: investigation?.uu_id,
      crimereference: incident?.uu_id as number,
      CR_number: incident?.crime_reference_number || "",
      Incident_Registration_number:
        incident?.Incident_Registration_number || "",
      court_files: undefined,
      prosecution_files: undefined,
      court_decision_date: undefined,
      court_decision_file: undefined,
      prosecution_decision_file: undefined,
      prosecutor_decision_date: undefined,
    })
      .then(() => {
        showTooltip(t("successfully_updated"), "success");
        setFormState(defaultEvidence);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit2 = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    editExhibit2({
      uu_id: formState.uu_id,
      is_exibit_retuned: true,
      exhibit_return_date: formState.exhibit_return_date,
      exhibit_returned_to: formState.exhibit_returned_to,
      exhibit_return_condition: formState.exhibit_return_condition,
      is_exbit_sold: formState.is_exbit_sold,
      exhibit_sale_date: formState.is_exbit_sold
        ? formState.exhibit_sale_date
        : undefined,
      exhibit_sale_amount: formState.is_exbit_sold
        ? formState.exhibit_sale_amount
        : undefined,
    })
      .then(() => {
        showTooltip(t("successfully_updated"), "success");
        setFormState(defaultEvidence);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFile = (file: File | File[] | null) => {
    setFormState({
      ...formState,
      exhibit_attachment_files: file
        ? Array.isArray(file)
          ? file
          : [file]
        : [],
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {isReturn ? "Return" : action} Exhibit File
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          publishing industries as a temporary filler.
        </p>

        {!isReturn ? (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Tool Type <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="tool_type"
                  value={formState?.tool_type}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Collected By <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="collected_by"
                  value={formState?.collected_by}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Unique Identification Code
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="unique_identification_code"
                  value={formState?.unique_identification_code}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Reference Number
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="reference_number"
                  value={formState?.reference_number}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Serial Number
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="serial_number"
                  value={formState?.serial_number}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Location Region
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="location_region"
                  value={formState?.location_region}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Location Zone
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="location_zone"
                  value={formState?.location_zone}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Location Woreda
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="location_woreda"
                  value={formState?.location_woreda}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Location House Number
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="location_housenumber"
                  value={formState?.location_housenumber}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Location Site
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="location_site"
                  value={formState?.location_site}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Person With Whom
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="person_with_whom"
                  value={formState?.person_with_whom}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Owner
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="owner"
                  value={formState?.owner}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Quantity Written
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="quantity_written"
                  value={formState?.quantity_written}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Exhibit Condition
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="exibit_condition"
                  value={formState?.exibit_condition}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Date collected <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="date_collected"
                  value={
                    formState?.date_collected
                      ? new Date(formState.date_collected)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Date of Exhibit
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="date_of_exhibit"
                  value={
                    formState?.date_of_exhibit
                      ? new Date(formState.date_of_exhibit)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Exhibit Condition
                </label>
                <select
                  className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                  name="exhibit_condition_when_submitted"
                  value={
                    typeof formState?.exhibit_condition_when_submitted ===
                    "string"
                      ? formState.exhibit_condition_when_submitted
                      : (formState?.exhibit_condition_when_submitted?.name ??
                        "")
                  }
                  onChange={handleChange}
                >
                  <option value={""}>Select Exhibit Condition</option>
                  {baseData?.exhibitConditions.map((type, index) => (
                    <option key={index} value={type?.name}>
                      {type?.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[500]">
                Asset Currently Found In
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300 "
                value={assetType}
                onChange={handleAssetTypeChange}
              >
                <option value="Person">Person</option>
                <option value="Government">Government</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {assetType === "Person" && (
                <>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect Full Name
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_fullname"
                      value={formState?.person_protect_fullname}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect Region
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_region"
                      value={formState?.person_protect_region}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect Zone
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_zone"
                      value={formState?.person_protect_zone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect Woreda
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_woreda"
                      value={formState?.person_protect_woreda}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect House Number
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_housenumber"
                      value={formState?.person_protect_housenumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect Phone Number
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_phonenumber"
                      value={formState?.person_protect_phonenumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Person Protect Site
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="person_protect_site"
                      value={formState?.person_protect_site}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}
              {/* Government Protect Fields */}
              {assetType === "Government" && (
                <>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect Name
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_name"
                      value={formState?.government_protect_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect Region
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_region"
                      value={formState?.government_protect_region}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect Zone
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_zone"
                      value={formState?.government_protect_zone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect Woreda
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_woreda"
                      value={formState?.government_protect_woreda}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect House Number
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_housenumber"
                      value={formState?.government_protect_housenumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect Phone Number
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_phonenumber"
                      value={formState?.government_protect_phonenumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Government Protect Site
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="government_protect_site"
                      value={formState?.government_protect_site}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_sample_collected"
                    checked={!!formState.is_sample_collected}
                    onChange={handleChange}
                    className="w-4 h-4"
                  />
                  Sample Collected?
                </label>
              </div>

              {formState.is_sample_collected ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Sample Collected By
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="sample_collected_by"
                      value={formState.sample_collected_by || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Sample Collection Date
                    </label>
                    <input
                      type="date"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="sample_collection_date"
                      value={
                        formState.sample_collection_date
                          ? new Date(formState.sample_collection_date)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Sample Amount
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="sample_amount"
                      value={formState.sample_amount || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Sample Location
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="sample_location"
                      value={formState.sample_location || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Analysis Result
                    </label>
                    <input
                      type="text"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="analysis_result"
                      value={formState.analysis_result || ""}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2 flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="is_sample_returned"
                        checked={!!formState.is_sample_returned}
                        onChange={handleChange}
                        className="w-4 h-4"
                      />
                      Sample Returned?
                    </label>
                  </div>
                  <div className="flex flex-col mt-4 w-full">
                    <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                      Sample Return Date
                    </label>
                    <input
                      type="date"
                      className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      name="sample_return_date"
                      value={
                        formState.sample_return_date
                          ? new Date(formState.sample_return_date)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                      onChange={handleChange}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Description
              </label>
              <textarea
                rows={4}
                className="w-full rounded-md border bg-[#F2F2F2] p-2 focus:border-blue-500 focus:outline-none focus:ring"
                name="description"
                value={formState?.description}
                onChange={handleChange}
              />
            </div>
            <div className="mt-4">
              <FileUploadMultiple
                label="Attachment Files"
                fileTypes="*"
                info="Upload PDF Files"
                multiple={true}
                required={true}
                onChange={handleFile}
              />
            </div>

            {/* Submit Button */}
            <div className="text-right">
              <button
                type="submit"
                className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                        text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                          ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon}
                    Loading...
                  </div>
                ) : (
                  action + " Exhibit"
                )}
              </button>
            </div>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit2}>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Exhibit Returned To
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="exhibit_returned_to"
                  value={formState?.exhibit_returned_to}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Exhibit Return Condition
                </label>
                <input
                  type="text"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="exhibit_return_condition"
                  value={formState?.exhibit_return_condition}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Exhibit Return Date
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="exhibit_return_date"
                  value={
                    formState?.exhibit_return_date
                      ? new Date(formState.exhibit_return_date)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="is_exbit_sold"
                    checked={!!formState.is_exbit_sold}
                    onChange={(e) => {
                      setFormState({
                        ...formState,
                        is_exbit_sold: e.target.checked,
                      });
                    }}
                    className="w-4 h-4"
                  />
                  Is Exhibit Sold?
                </label>
              </div>

              {formState.is_exbit_sold && (
                <div className="flex flex-col  w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Date of Exhibit
                  </label>
                  <input
                    type="date"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="exhibit_sale_date"
                    value={
                      formState?.exhibit_sale_date
                        ? new Date(formState.exhibit_sale_date)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>
              )}
              {formState.is_exbit_sold && (
                <div className="flex flex-col mt-4 w-full">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Exhibit Sale Amount
                  </label>
                  <input
                    type="number"
                    name="exhibit_sale_amount"
                    value={formState?.exhibit_sale_amount}
                    onChange={handleChange}
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  />
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="text-right">
              <button
                type="submit"
                className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                        text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                          ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon}
                    Loading...
                  </div>
                ) : (
                  "Return"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ExhibitForm;
